/**
 * Métricas de ciclo partilhadas entre Início e Despesas.
 * Fonte: /budgets/overview/ + /income/monthly-summary/ (mesmo ciclo selfie).
 */

/**
 * Poupança efectiva: apenas o envelope não atribuído a categorias dentro do grupo PYF.
 * Categorias com orçamento dentro do PYF (ex: despesas permitidas) reduzem a reserva real.
 * @param {object | null | undefined} overview
 * @returns {number}
 */
function cycleEffectiveSavingsEur(overview) {
  const pyfGroup = Array.isArray(overview?.groups)
    ? overview.groups.find((g) => g.is_pay_yourself_first)
    : null;

  if (pyfGroup) {
    // envelope_unassigned_eur = grupo total − alocado a categorias específicas
    if (pyfGroup.envelope_unassigned_eur != null) {
      return Math.max(0, Number(pyfGroup.envelope_unassigned_eur));
    }
    const groupTotal = Number(
      pyfGroup.reserved_eur ?? pyfGroup.target_budget_eur ?? pyfGroup.budget_eur ?? 0,
    );
    const allocated = Number(pyfGroup.categories_budget_sum_eur ?? 0);
    return Math.max(0, groupTotal - allocated);
  }

  return Number(overview?.method_savings_eur ?? 0);
}

/**
 * Orçamento total gastável no ciclo (rendimento **registado** − poupança efectiva).
 * Categorias de despesa dentro do grupo PYF aumentam o pool gastável.
 *
 * Usa `registered_cycle_income_eur` (rendimento realmente lançado neste ciclo),
 * nunca `cycle_income_eur` — este último pode ser uma estimativa herdada do
 * ciclo anterior (`income_basis: "previous_cycle"`) quando ainda não lançaste
 * nenhum rendimento no ciclo actual, e não é dinheiro disponível de facto.
 *
 * @param {object | null | undefined} overview
 * @returns {number}
 */
export function cycleTotalSpendableEur(overview) {
  const income = Number(overview?.registered_cycle_income_eur ?? 0);
  if (!Number.isFinite(income) || income <= 0) return 0;
  return income - cycleEffectiveSavingsEur(overview);
}

/**
 * Quanto ainda podes gastar neste ciclo.
 *
 * Não usar `available_to_budget_eur`: essa métrica é margem de planeamento
 * (rendimento − reserva − orçamentos alocados) e **não muda** quando registas
 * uma despesa. O utilizador espera rendimento − reserva real − gasto real.
 *
 * Não usar `cycle_income_eur`: ver nota em `cycleTotalSpendableEur` — pode ser
 * o rendimento do ciclo anterior, não rendimento real lançado neste ciclo.
 * Sem rendimento registado neste ciclo devolve `null` (sem dados), em vez de
 * assumir o valor do ciclo anterior como disponível para gastar.
 *
 * @param {object | null | undefined} overview — payload de /budgets/overview/
 * @returns {number | null}
 */
export function cycleSpendableRemainingEur(overview) {
  const income = Number(overview?.registered_cycle_income_eur ?? 0);
  if (!Number.isFinite(income) || income <= 0) return null;

  const spent = Number(overview.total_spent_eur ?? 0);
  if (!Number.isFinite(spent)) return null;

  const effectiveSavings = cycleEffectiveSavingsEur(overview);
  return income - effectiveSavings - spent;
}

/**
 * Taxa de poupança do ciclo (monthly-summary), ou null se não houver rendimento.
 * @param {object | null | undefined} monthlySummary
 * @returns {number | null}
 */
export function cycleSavingsRatePct(monthlySummary) {
  const savingsPctRaw = monthlySummary?.savings_rate_pct;
  const hasSavingsRate =
    savingsPctRaw !== null &&
    savingsPctRaw !== undefined &&
    Number(monthlySummary?.income?.total || 0) > 0;
  if (!hasSavingsRate) return null;
  const parsed = Number(savingsPctRaw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Valor efectivamente reservado para poupança/investimento (grupo PYF).
 * Lê reserved_eur do grupo pay_yourself_first em overview.groups;
 * fallback para method_savings_eur se o grupo não existir.
 * @param {object | null | undefined} overview
 * @returns {number | null}
 */
export function cycleSavingsReservedEur(overview) {
  if (!overview?.has_income) return null;

  const pyfGroup = Array.isArray(overview.groups)
    ? overview.groups.find((g) => g.is_pay_yourself_first)
    : null;

  // envelope_unassigned_eur = linha "Reservado" no accordion (não alocado a cofres)
  const raw = pyfGroup
    ? (pyfGroup.envelope_unassigned_eur ??
        // fallback: grupo total menos alocado a categorias
        (pyfGroup.reserved_eur != null && pyfGroup.categories_budget_sum_eur != null
          ? Math.max(0, Number(pyfGroup.reserved_eur) - Number(pyfGroup.categories_budget_sum_eur))
          : pyfGroup.reserved_eur ?? pyfGroup.target_budget_eur ?? pyfGroup.budget_eur))
    : (overview.method_savings_eur ?? overview.pay_yourself_eur);

  // Subtrair overspend de categorias nomeadas dentro do grupo PYF —
  // espelha o cálculo da linha "Reservado" no accordion (ExpensesByGroupList).
  const categoriesOverspend = pyfGroup
    ? (pyfGroup.categories || []).reduce((sum, cat) => {
        const spent = Number(cat.spent_eur ?? 0);
        const budget = cat.budget_eur != null ? Number(cat.budget_eur) : 0;
        return sum + Math.max(0, spent - budget);
      }, 0)
    : 0;

  const reserved = Math.max(0, Number(raw ?? 0) - categoriesOverspend);
  if (!Number.isFinite(reserved) || reserved <= 0) return null;
  return reserved;
}
